import { useState } from 'react';

import {
  ActionDialogButtons,
  ActionDialogContent,
  Button,
  Card,
  Dialog,
  DialogContent,
  IconButton,
  Meter,
  OffsetPagination,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@opositandopn/ui';
import classnames from 'classnames';
import {
  IoDocumentTextOutline,
  IoPlayCircleOutline,
  IoPushOutline,
  IoTimeOutline,
  IoTrashBinOutline,
} from 'react-icons/io5';

import { Empty } from '../../../../components/core/Empty';
import { Loading } from '../../../../components/core/Loading';
import { BaseComponentProps } from '../../../../types/base-component.types';
import {
  formatHourDate,
  formatTimestamp,
  lastMonthsRange,
} from '../../../../utils/date';
import {
  AttemptMode,
  QuestionAttemptsViewModel,
  useDiscardMultipleChoiceAssessmentAttempt,
  useGetMultipleChoiceAssessmentAttempts,
} from '../../api/useMultipleChoiceAssessments';

import styles from './MultipleChoicePausedAssessmentAttemptList.module.scss';

const MONTHS_OF_HISTORY = 3;

/** The share of the questions already answered, as the percentage the meter takes. */
function toProgress({ answered, total }: QuestionAttemptsViewModel) {
  return total > 0 ? (answered / total) * 100 : 0;
}

type ContainerProps = BaseComponentProps & {
  subjectId: string;
  onSelected?: (assessmentAttemptId: string, mode?: AttemptMode) => void;
};

function MultipleChoicePausedAssessmentAttemptList({
  className,
  subjectId,
  onSelected,
  testId = 'ui-multiple-choice-paused-assessment-attempt-list',
}: ContainerProps) {
  const [attemptToDiscard, setAttemptToDiscard] = useState<string>();

  const { mutate: discardMultipleChoiceAssessmentAttempt, isPending } =
    useDiscardMultipleChoiceAssessmentAttempt();
  const [dateRange] = useState<[Date, Date]>(() =>
    lastMonthsRange(MONTHS_OF_HISTORY),
  );
  const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 10 });

  const { data: response, isLoading } = useGetMultipleChoiceAssessmentAttempts({
    subjectId: subjectId,
    pageNumber: pagination.pageNumber,
    pageSize: pagination.pageSize,
    fromTime: dateRange[0],
    toTime: dateRange[1],
    sort: '-createdAt',
    params: {
      // An attempt left mid-submission belongs here too: its sitting is over but its
      // answers are still on paper, and this is where they get uploaded.
      statuses: ['paused', 'submitting'],
    },
  });

  if (isLoading || !response) {
    return <Loading />;
  }

  if (response.totalItems === 0) {
    return <Empty title="No tienes simulacros en pausa" />;
  }

  return (
    <div className={classnames(styles['main'], className)} data-testid={testId}>
      <Card title="Simulacros pausados">
        {response.items.map((item) => {
          const isTranscribing = item.status === 'submitting';

          return (
            <div className={styles['item']} key={item.id}>
              <div className={styles['summary']}>
                <div className={styles['description']}>
                  <Text className={styles['name']} font="heading">
                    {item.name}
                  </Text>
                  <div className={styles['meta']}>
                    <Text variety="annotation" font="monospace">
                      {formatHourDate(item.createdAt, item.timeZoneId)}
                    </Text>
                    <span className={styles['elapsed']}>
                      <IoTimeOutline aria-hidden={true} />
                      <Text variety="annotation" font="monospace">
                        {formatTimestamp(item.elapsedTime)}
                      </Text>
                    </span>
                    {item.mode === 'manual' && (
                      <span className={styles['paper']}>
                        <IoDocumentTextOutline aria-hidden={true} />
                        <Text variety="annotation">
                          {isTranscribing
                            ? 'Pendiente de subir respuestas'
                            : 'Simulacro en papel'}
                        </Text>
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles['actions']}>
                  {onSelected && (
                    <Tooltip placement="left" mouseOnly={true}>
                      <TooltipTrigger asChild={true}>
                        <IconButton
                          size="sm"
                          icon={
                            isTranscribing ? (
                              <IoPushOutline />
                            ) : (
                              <IoPlayCircleOutline />
                            )
                          }
                          aria-label={
                            isTranscribing
                              ? 'Subir respuestas'
                              : 'Continuar simulacro'
                          }
                          onClick={() => onSelected(item.id, item.mode)}
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        {isTranscribing
                          ? 'Subir respuestas'
                          : 'Continuar simulacro'}
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {/* <IconButton
                  size="sm"
                  icon={<IoTrashBinOutline />}
                  aria-label="Descartar"
                  onClick={() => setAttemptToDiscard(item.id)}
                /> */}
                </div>
              </div>
              {item.mode !== 'manual' && (
                <div className={styles['progress']}>
                  <Meter
                    className={styles['meter']}
                    variant="success"
                    value={toProgress(item.questionAttempts)}
                    aria-label="Preguntas respondidas"
                  />
                  <Text variety="annotation" font="monospace">
                    {item.questionAttempts.answered}/
                    {item.questionAttempts.total}
                  </Text>
                </div>
              )}
            </div>
          );
        })}
      </Card>
      <OffsetPagination
        pageNumber={pagination.pageNumber}
        pageSize={pagination.pageSize}
        totalRecords={response.totalItems}
        onPageChange={(pageNumber: number) =>
          setPagination((prev) => ({ ...prev, pageNumber }))
        }
        pageNeighbours={2}
      />
      <Dialog
        open={attemptToDiscard !== undefined}
        onOpenChange={(open) => !open && setAttemptToDiscard(undefined)}
      >
        <DialogContent>
          <ActionDialogContent
            icon={<IoTrashBinOutline />}
            title="¿Estás segurx de que deseas descartar el simulacro?"
            subtitle="Se perderán las respuestas que hayas dado y no podrás retomarlo"
          >
            <ActionDialogButtons>
              <Button
                variant="outline-primary"
                onClick={() => setAttemptToDiscard(undefined)}
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                loading={isPending}
                disabled={isPending}
                onClick={() => {
                  discardMultipleChoiceAssessmentAttempt(
                    {
                      subjectId: subjectId,
                      assessmentAttemptId: attemptToDiscard!,
                    },
                    {
                      onSuccess: () => {
                        setAttemptToDiscard(undefined);
                      },
                      onError: () => {
                        setAttemptToDiscard(undefined);
                      },
                    },
                  );
                }}
              >
                Descartar
              </Button>
            </ActionDialogButtons>
          </ActionDialogContent>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { MultipleChoicePausedAssessmentAttemptList };
